/*
 * Renderer: daterange  –  type="daterange"
 *
 * Calendar picker for selecting a start and end date (a date range).
 * Click once to set the start date; hover shows a live preview of the highlighted range;
 * click again to confirm the end date and close the panel.
 * Fires `input-change` (bubbles, composed) with detail { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }.
 * Use getValue() / setValue({ from, to }) to read or set the range programmatically.
 *
 * Attributes:
 *   min      – earliest selectable date (YYYY-MM-DD)
 *   max      – latest selectable date (YYYY-MM-DD)
 *   no-clear – hides the "Löschen" (clear) button in the footer
 *   disabled – disables all interaction
 */
import { formatDateISO, fmtDE } from '../utils/dateUtils.js';

export const DaterangeRenderer = {
    renderDaterange() {
        const placeholder = this.getAttribute('placeholder') || 'Zeitraum wählen';
        this.clearShadow();

        if (!this.rangeMonth) {
            const now = new Date();
            this.rangeMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        const trigger = this.buildTrigger(
            (this.rangeStart && this.rangeEnd)
                ? `${fmtDE(this.rangeStart)} – ${fmtDE(this.rangeEnd)}`
                : placeholder,
            'calendar',
        );
        const panel = document.createElement('div');
        panel.className = 'inputPanel calPanel daterangePanel';
        panel.hidden    = true;

        this.trigger = trigger;
        this.panel   = panel;
        this.buildRangePanel();

        trigger.addEventListener('click', e => { e.stopPropagation(); this.togglePanel(); });
        trigger.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.togglePanel(); }
            if (e.key === 'Escape') { this.closePanel(); }
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.isOpen) {
                e.preventDefault();
                this.openPanel();
            }
        });

        panel.addEventListener('mouseleave', () => {
            if (!this.rangeHover) return;
            this.rangeHover = null;
            panel.querySelectorAll('.calDay').forEach(c => {
                c.classList.remove('calInRange');
                if (c.dataset.date !== this.rangeStart) c.classList.remove('calRangeStart');
                if (c.dataset.date !== this.rangeEnd)   c.classList.remove('calRangeEnd');
            });
            if (this.rangeStart && this.rangeEnd) {
                this.applyRangeHighlights(panel, this.rangeStart, this.rangeEnd);
            }
        });

        this.shadowRoot.appendChild(trigger);
        this.shadowRoot.appendChild(panel);
        this.attachOutsideHandler();
    },

    buildRangePanel() {
        const panel = this.panel;
        if (!panel) return;
        panel.innerHTML = '';

        const viewMonth = this.rangeMonth;
        const minDate = this.getAttribute('min') || null;
        const maxDate = this.getAttribute('max') || null;

        const prevMonthLastDay  = formatDateISO(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 0));
        const nextMonthFirstDay = formatDateISO(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
        const prevBlocked = minDate && prevMonthLastDay < minDate;
        const nextBlocked = maxDate && nextMonthFirstDay > maxDate;

        const goPrev = prevBlocked ? null : () => {
            this.rangeMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1);
            this.buildRangePanel();
            this.focusRangeDay();
        };
        const goNext = nextBlocked ? null : () => {
            this.rangeMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
            this.buildRangePanel();
            this.focusRangeDay();
        };

        panel.appendChild(this.buildCalHeader(this.rangeMonth, goPrev, goNext));

        const onDayClick = (dateStr) => {
            if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
                this.rangeStart = dateStr;
                this.rangeEnd   = null;
                this.rangeHover = null;
            } else {
                if (dateStr < this.rangeStart) {
                    this.rangeEnd   = this.rangeStart;
                    this.rangeStart = dateStr;
                } else {
                    this.rangeEnd = dateStr;
                }
                this.rangeHover = null;
                this.updateRangeTrigger();
                this.closePanel();
                this.dispatchEvent(new CustomEvent('input-change', {
                    bubbles: true, composed: true,
                    detail: { from: this.rangeStart, to: this.rangeEnd },
                }));
            }
            this.buildRangePanel();
        };

        const grid = this.buildCalGrid(this.rangeMonth, onDayClick, {
            rangeStart: this.rangeStart,
            rangeEnd:   this.rangeEnd,
        }, { goPrev, goNext });
        panel.appendChild(grid);

        if (this.rangeStart && !this.rangeEnd) {
            panel.querySelectorAll('.calDay[data-date]').forEach(c => {
                if (c.dataset.date > this.rangeStart) c.classList.add('calInRange', 'calInRangePending');
            });
            panel.querySelector(`.calDay[data-date="${this.rangeStart}"]`)?.classList.add('calRangeStart');
        }

        panel.querySelectorAll('.calDay:not(.calDayEmpty)').forEach(cell => {
            cell.addEventListener('mouseenter', () => {
                if (!this.rangeStart || this.rangeEnd) return;
                const hStr = cell.dataset.date;
                if (hStr === this.rangeHover) return;
                this.rangeHover = hStr;
                panel.querySelectorAll('.calDay').forEach(c => {
                    c.classList.remove('calInRange', 'calInRangePending', 'calRangeStart', 'calRangeEnd');
                });
                panel.querySelector(`.calDay[data-date="${this.rangeStart}"]`)?.classList.add('calRangeStart');
                cell.classList.add('calRangeEnd');
                this.applyRangeHighlights(panel, this.rangeStart, hStr);
            });
        });

        const footer = document.createElement('div');
        footer.className = 'calFooter';
        if (!this.hasAttribute('no-clear')) {
            const clearBtn = document.createElement('button');
            clearBtn.className   = 'inputBtn calClearBtn';
            clearBtn.type        = 'button';
            clearBtn.textContent = 'Löschen';
            clearBtn.addEventListener('click', e => {
                e.stopPropagation();
                this.rangeStart = null;
                this.rangeEnd   = null;
                this.updateRangeTrigger();
                this.dispatchEvent(new CustomEvent('input-change', {
                    bubbles: true, composed: true,
                    detail: { from: null, to: null },
                }));
                this.closePanel();
                this.trigger?.focus();
            });
            clearBtn.addEventListener('keydown', e => {
                if (e.key === 'Escape') { e.stopPropagation(); this.closePanel(); this.trigger?.focus(); }
            });
            footer.appendChild(clearBtn);
        }
        panel.appendChild(footer);
    },

    focusRangeDay() {
        if (!this.isOpen) return;
        requestAnimationFrame(() => {
            const target = this.panel?.querySelector(
                `.calDay[data-date="${this.rangeStart}"], .calDay.calToday, .calDay:not(.calDayEmpty)`
            );
            target?.focus();
        });
    },

    applyRangeHighlights(panel, start, end) {
        if (!start || !end) return;
        const rangeLow  = start < end ? start : end;
        const rangeHigh = start < end ? end   : start;
        panel.querySelectorAll('.calDay[data-date]').forEach(cell => {
            const cellDate = cell.dataset.date;
            if (cellDate > rangeLow && cellDate < rangeHigh) cell.classList.add('calInRange');
        });
    },

    updateRangeTrigger() {
        const label = this.trigger?.querySelector('.inputTriggerLabel');
        if (!label) return;
        const placeholder = this.getAttribute('placeholder') || 'Zeitraum wählen';
        const hasRange = !!(this.rangeStart && this.rangeEnd);
        label.textContent = hasRange ? `${fmtDE(this.rangeStart)} – ${fmtDE(this.rangeEnd)}` : placeholder;
        label.classList.toggle('is-placeholder', !hasRange);
    },
};
